import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { onValue, ref } from "firebase/database";
import { auth, db } from "../api/firebase";
import "../styles/journey.css";
import "../styles/productDetails.css";

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export default function MyEcards() {
  const [uid, setUid] = useState("");
  const [cards, setCards] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!auth) return undefined;
    const unsub = onAuthStateChanged(auth, (u) => {
      setUid(u?.uid || "");
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!uid || !db) return undefined;
    const r = ref(db, `users/${uid}/ecard_drafts`);
    const unsub = onValue(
      r,
      (snap) => {
        const raw = snap.val() || {};
        const rows = Object.entries(raw)
          .map(([id, row]) => ({ id, ...(row || {}) }))
          .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
        setCards(rows);
        setErr("");
      },
      (e) => {
        setErr(e?.message || "Could not load your e-cards.");
      }
    );
    return () => unsub();
  }, [uid]);

  const mapped = useMemo(
    () =>
      cards.map((c) => {
        const gift = c.gift || {};
        const isPublished = c.status === "published" && c.shortCode;
        return {
          ...c,
          title: gift.Tittle || "Untitled e-card",
          recipient: gift.reciever || "Recipient",
          sender: gift.Name || "You",
          message: gift.Message || "",
          publicUrl: isPublished
            ? `${window.location.origin}/s/${c.shortCode}`
            : null,
        };
      }),
    [cards]
  );

  return (
    <div className="pd-wrap">
      <div className="journey-panel">
        <h1 style={{ marginTop: 0 }}>My eCards</h1>
        <p className="journey-share-lead">
          View cards you created, their status, and open share links for published cards.
        </p>

        {err ? <p className="pd-error">{err}</p> : null}
        {!uid ? (
          <div className="journey-summary-card">
            <p style={{ margin: 0 }}>Please login to view your e-cards.</p>
            <Link to="/login" state={{ nextPath: "/my-ecards" }} className="journey-link-back" style={{ marginTop: "0.75rem" }}>
              Go to login
            </Link>
          </div>
        ) : null}

        {uid && !mapped.length ? (
          <div className="journey-summary-card">
            <p style={{ margin: 0 }}>No e-cards found yet.</p>
            <Link to="/" className="journey-link-back" style={{ marginTop: "0.75rem" }}>
              Create your first e-card
            </Link>
          </div>
        ) : uid ? (
          <div className="journey-checkout-grid">
            {mapped.map((c) => (
              <article key={c.id} className="journey-summary-card">
                <h2 style={{ marginTop: 0 }}>{c.title}</h2>
                <p className="journey-price-line">
                  <strong>To:</strong> {c.recipient}
                </p>
                <p className="journey-price-line">
                  <strong>From:</strong> {c.sender}
                </p>
                <p className="journey-price-line">
                  <strong>Status:</strong> {c.status || "private"}
                </p>
                <p className="journey-hint-small">
                  Created: <strong>{formatDate(c.createdAt)}</strong>
                </p>
                {c.publicUrl ? (
                  <p className="journey-hint-small">
                    Public link:{" "}
                    <a href={c.publicUrl} target="_blank" rel="noreferrer">
                      Open
                    </a>
                  </p>
                ) : (
                  <p className="journey-hint-small">Public link will appear after publish.</p>
                )}
              </article>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
